import React, { useMemo } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  Area,
  AreaChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { LivestreamBrandMention } from "./LivestreamTimelineBlock";
import { prepareAudienceRetentionChartData } from "./audienceRetentionChartData";
import type { AudienceRetentionRow } from "./audienceRetentionChartData";

interface AudienceRetentionTimelineBlockProps {
  durationSeconds: number;
  audienceRetention: AudienceRetentionRow[];
  averageViewPercentage?: number | null;
  averageViewDuration?: number | null;
  brandMentions?: LivestreamBrandMention[];
}

const formatTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
};

const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }
  return `${minutes}:${secs.toString().padStart(2, "0")}`;
};

const RetentionTooltip: React.FC<{
  active?: boolean;
  payload?: Array<{ payload: { timeSeconds: number; retentionPct: number } }>;
}> = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload;
  if (!point) return null;

  return (
    <Box
      sx={{
        p: 1.5,
        maxWidth: 200,
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        boxShadow: 2,
      }}
    >
      <Typography sx={{ fontWeight: 600, fontSize: "0.75rem", mb: 0.5 }}>
        {formatTime(point.timeSeconds)}
      </Typography>
      <Typography sx={{ fontSize: "0.8rem", fontWeight: 600 }}>
        {point.retentionPct.toFixed(1)}% retención
      </Typography>
    </Box>
  );
};

export const AudienceRetentionTimelineBlock: React.FC<
  AudienceRetentionTimelineBlockProps
> = ({
  durationSeconds,
  audienceRetention,
  averageViewPercentage,
  averageViewDuration,
  brandMentions = [],
}) => {
  const theme = useTheme();
  const chartLine = theme.palette.primary.main;
  const chartAxisTickFill = theme.palette.text.secondary;
  const mentionLineColor = theme.palette.text.secondary;
  const chartPlotBg = "surfaceMuted";
  const statPillBg = "surfaceMuted";
  const chartGradientOverlay = `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.04)} 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`;

  const { hasRetentionData, chartPoints } = useMemo(
    () =>
      prepareAudienceRetentionChartData({
        audienceRetention,
        durationSeconds,
      }),
    [audienceRetention, durationSeconds],
  );

  if (!hasRetentionData) {
    return null;
  }

  const showStats =
    averageViewPercentage != null || averageViewDuration != null;

  return (
    <Box sx={{ mb: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: "0.9rem",
            fontWeight: 700,
            color: "text.primary",
            letterSpacing: "0.05em",
          }}
        >
          Retención de audiencia
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Typography
            sx={{
              fontSize: "0.7rem",
              color: "text.secondary",
              fontFamily: "monospace",
            }}
          >
            0:00
          </Typography>
          <Typography sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
            →
          </Typography>
          <Typography
            sx={{
              fontSize: "0.7rem",
              color: "text.secondary",
              fontFamily: "monospace",
            }}
          >
            {formatDuration(durationSeconds)}
          </Typography>
        </Box>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 2,
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
        }}
      >
        <Box
          sx={{
            position: "relative",
            height: 200,
            bgcolor: chartPlotBg,
            borderRadius: 3,
            overflow: "visible",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: chartGradientOverlay,
              borderRadius: 3,
              zIndex: 0,
            }}
          />
          <Box sx={{ position: "absolute", inset: 0, zIndex: 2 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={chartPoints}
                margin={{ top: 8, right: 20, bottom: 0, left: 8 }}
              >
                <defs>
                  <linearGradient
                    id="retentionGradient"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop
                      offset="0%"
                      stopColor={chartLine}
                      stopOpacity={0.35}
                    />
                    <stop
                      offset="100%"
                      stopColor={chartLine}
                      stopOpacity={0.06}
                    />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="timeSeconds"
                  hide
                  domain={[0, durationSeconds]}
                  type="number"
                />
                <YAxis
                  domain={[0, 100]}
                  tickFormatter={(v) => `${v}%`}
                  tick={{ fontSize: 10, fill: chartAxisTickFill }}
                  width={40}
                />
                <ChartTooltip content={<RetentionTooltip />} />
                <Area
                  type="monotone"
                  dataKey="retentionPct"
                  stroke={chartLine}
                  strokeWidth={1.5}
                  fill="url(#retentionGradient)"
                />
                {brandMentions.map((mention, idx) => (
                  <ReferenceLine
                    key={`retention-mention-${idx}`}
                    x={mention.mentionStartTime}
                    stroke={mentionLineColor}
                    strokeWidth={2}
                    strokeDasharray="4 2"
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          </Box>
        </Box>

        {showStats && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr 1fr", md: "1fr 1fr" },
              gap: 2,
              mt: 2,
            }}
          >
            {averageViewPercentage != null && (
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: statPillBg,
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", fontWeight: 600 }}
                >
                  Retención promedio
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 700, color: "primary.main" }}
                >
                  {averageViewPercentage.toFixed(1)}%
                </Typography>
              </Box>
            )}
            {averageViewDuration != null && averageViewDuration > 0 && (
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: statPillBg,
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", fontWeight: 600 }}
                >
                  Duración promedio de visualización
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 700, color: "primary.main" }}
                >
                  {formatDuration(averageViewDuration)}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
};
